import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCode, FiBrain, FiTarget, FiShield, FiSettings, FiZap, FiTrendingUp, FiCheck } = FiIcons;

const Services = () => {
  const navigate = useNavigate();

  const handleGetQuote = () => {
    navigate('/contact');
  };

  const handleScheduleConsultation = () => {
    navigate('/contact');
  };

  const handleViewPortfolio = () => {
    navigate('/case-studies');
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      icon: FiZap,
      title: "Rapid Application Development",
      subtitle: "From Concept to Launch, Faster Than Ever",
      description: "In the competitive digital landscape, speed to market is paramount. Our rapid application development services, powered by AI, drastically cut down traditional development cycles without sacrificing quality. We streamline every stage – from ideation and prototyping to deployment and iteration – ensuring your vision becomes a reality at an accelerated pace.",
      features: [
        "AI-Powered Prototyping: Quickly generate and refine UI/UX mockups and prototypes based on your requirements",
        "Automated Code Generation: Utilize AI tools to generate boilerplate code, specific functions, and even complex logic",
        "Agile AI-Assisted Sprints: Integrate AI into our agile methodology for faster iteration and feedback loops",
        "Efficient Resource Allocation: AI helps optimize task assignments and resource management, preventing bottlenecks"
      ],
      benefits: [
        "Faster Time-to-Market: Launch your products and features sooner, gaining a competitive edge",
        "Reduced Development Costs: Minimize labor hours and maximize efficiency with AI-driven workflows",
        "High-Quality MVPs: Deliver robust minimum viable products ready for user feedback and scaling"
      ]
    },
    {
      icon: FiBrain,
      title: "AI-Enhanced Code Generation & Optimization",
      subtitle: "Smarter Code, Superior Performance",
      description: "This is the core of our expertise. We go beyond basic automation, employing advanced AI methodologies and expert prompt engineering to enhance every aspect of your codebase. Our solutions ensure your applications are not just built quickly, but are also robust, secure, and scalable.",
      features: [
        "Intelligent Code Generation: AI tools assist in writing clean, efficient, and maintainable code across various languages and frameworks",
        "Performance Optimization: AI identifies and suggests improvements for code efficiency, reducing load times and enhancing user experience",
        "Automated Testing & Quality Assurance: AI-driven testing frameworks identify bugs early, perform comprehensive test coverage, and validate application behavior rigorously",
        "Security Vulnerability Detection: Proactively identify and mitigate security risks with AI-powered code analysis tools, ensuring a fortified application"
      ],
      benefits: [
        "Exceptional Code Quality: Benefit from highly optimized, reliable, and bug-resistant applications",
        "Future-Proof Architecture: Build scalable solutions designed for long-term growth and easy maintenance",
        "Reduced Technical Debt: Proactive AI assistance minimizes errors and ensures a cleaner codebase from the start"
      ]
    },
    {
      icon: FiTarget,
      title: "Prompt Engineering & AI Integration",
      subtitle: "Harnessing AI's Full Potential",
      description: "The true power of AI lies in how effectively it's directed. Our specialists are experts in prompt engineering, crafting precise instructions for AI models to ensure accurate, context-aware code and functionality generation. Beyond coding, we seamlessly integrate AI capabilities into your existing systems and business processes, unlocking new levels of intelligence and automation.",
      features: [
        "Custom Prompt Development: Tailored prompts to generate specific code, features, or data processing logic",
        "AI Model Fine-Tuning: Optimizing AI models for your specific domain and project requirements",
        "API Integration: Connecting your applications with leading AI services and custom models",
        "Strategic AI Consultation: Guiding you on where and how AI can deliver the most significant impact on your business"
      ],
      benefits: [
        "Precise AI Output: Get exactly what you need with expertly crafted prompts",
        "Seamless Integration: AI capabilities that work harmoniously with your existing systems",
        "Strategic Advantage: Leverage AI to solve complex business challenges and create competitive advantages"
      ]
    },
    {
      icon: FiShield,
      title: "Code Auditing & Refinement",
      subtitle: "Ensuring Excellence and Longevity",
      description: "Even the best code can benefit from expert review. Our AI-assisted code auditing services provide a thorough examination of your existing codebase (or new projects), identifying areas for improvement in performance, security, maintainability, and scalability. We then refine and optimize, ensuring your software foundation is solid and future-proof.",
      features: [
        "Security Audits: Identify and mitigate vulnerabilities",
        "Performance Optimization: Pinpoint bottlenecks and suggest improvements",
        "Code Quality & Readability: Ensure adherence to best practices and coding standards",
        "Scalability Assessment: Evaluate the code's ability to handle future growth",
        "Technical Debt Reduction: Strategically refactor and modernize legacy or complex code"
      ],
      benefits: [
        "Enhanced Security: Protect your application and user data with comprehensive security reviews",
        "Improved Performance: Faster, more efficient applications that deliver better user experiences",
        "Maintainable Codebase: Clean, well-documented code that's easy to update and extend"
      ]
    },
    {
      icon: FiSettings,
      title: "Ongoing Maintenance & Support",
      subtitle: "Your Partner Beyond Launch",
      description: "Launching your application is just the beginning. We provide comprehensive ongoing maintenance and support services to ensure your software remains high-performing, secure, and up-to-date. Our AI-assisted monitoring tools proactively identify issues, allowing us to provide rapid, effective solutions and continuous enhancements.",
      features: [
        "Proactive Monitoring: AI-powered systems detect and alert for potential issues before they impact users",
        "Bug Fixes & Patch Management: Rapid response to any issues, ensuring minimal downtime",
        "Performance Tuning: Continuous optimization to maintain peak application performance",
        "Security Updates: Regular patching and security enhancements to protect against new threats",
        "Feature Enhancements: Implementing new features and improvements based on user feedback and evolving business needs"
      ],
      benefits: [
        "Peace of Mind: Know that your application is being actively monitored and maintained",
        "Minimal Downtime: Proactive issue detection and rapid response times",
        "Continuous Improvement: Regular updates and enhancements to keep your application competitive"
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our AI-Accelerated Services
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Innovation Delivered with Speed and Precision. At aiximo.com, we don't just build software; we engineer the future of development. Our services are meticulously designed to harness the power of AI, offering unparalleled speed, quality, and cost-efficiency across your entire project lifecycle.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12`}
              >
                <div className="flex-1">
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mr-6">
                      <SafeIcon icon={service.icon} className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">{service.title}</h2>
                      <p className="text-lg text-blue-600 font-medium">{service.subtitle}</p>
                    </div>
                  </div>

                  <p className="text-gray-600 text-lg leading-relaxed mb-8">
                    {service.description}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">How We Achieve It:</h4>
                      <ul className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Benefits for You:</h4>
                      <ul className="space-y-3">
                        {service.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start">
                            <SafeIcon icon={FiCheck} className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex-1 lg:max-w-md">
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-2xl">
                    <div className="w-24 h-24 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon={service.icon} className="h-12 w-12 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 text-center mb-4">
                      Ready to get started?
                    </h3>
                    <p className="text-gray-600 text-center mb-6">
                      Let's discuss how this service can transform your project.
                    </p>
                    <button
                      onClick={handleGetQuote}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                    >
                      Get a Quote
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Transform Your Development Process?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Choose from our comprehensive AI-accelerated services or let us create a custom solution for your unique needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleScheduleConsultation}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Schedule a Consultation
              </button>
              <button
                onClick={handleViewPortfolio}
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-600 hover:text-white transition-all duration-300"
              >
                View Our Portfolio
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;